import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as openpgp from 'openpgp';

const Compose = () => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [from, setFrom] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [pgpKeys, setPgpKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [recipientPublicKey, setRecipientPublicKey] = useState('');
  const [encryptEnabled, setEncryptEnabled] = useState(false);
  const [signEnabled, setSignEnabled] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sending, setSending] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('addison_token');

  useEffect(() => {
    fetchPgpKeys();
    fetchEmailAccounts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (to || subject || body) saveDraft();
    }, 30000);
    return () => clearTimeout(timer);
  }, [to, cc, bcc, subject, body]);

  const fetchEmailAccounts = async () => {
    try {
      const response = await axios.get('/api/email-accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const accounts = response.data.accounts || [];
      setEmailAccounts(accounts);
      if (accounts.length > 0 && !from) {
        setFrom(accounts[0].email_address);
      }
    } catch (err) {
      console.error('Failed to fetch email accounts:', err);
      const username = localStorage.getItem('username');
      if (username) {
        setFrom(`${username}@addisonelectronique.com`);
      }
    }
  };

  const fetchPgpKeys = async () => {
    try {
      const response = await axios.get('/api/pgp/keys', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const keys = response.data.keys || [];
      setPgpKeys(keys);
      const defaultKey = keys.find(k => k.is_default);
      if (defaultKey) setSelectedKey(defaultKey);
      else if (keys.length > 0) setSelectedKey(keys[0]);
    } catch (err) {
      console.error('Failed to fetch PGP keys:', err);
    }
  };

  const saveDraft = () => {
    const draft = { to, cc, bcc, subject, body, from, attachments };
    localStorage.setItem('emailDraft', JSON.stringify(draft));
    setSavedDraft(true);
    setTimeout(() => setSavedDraft(false), 2000);
  };

  const handleAttachment = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments(prev => [...prev, {
          name: file.name,
          type: file.type,
          size: file.size,
          data: event.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const encryptMessage = async () => {
    try {
      const publicKey = await openpgp.readKey({ armoredKey: recipientPublicKey });
      const encrypted = await openpgp.encrypt({
        message: await openpgp.createMessage({ text: body }),
        encryptionKeys: publicKey
      });
      return encrypted;
    } catch (err) {
      console.error('Encryption failed:', err);
      throw new Error('Failed to encrypt message: ' + err.message);
    }
  };

  const handleSend = async () => {
    if (!to || !subject || !body || !from) {
      setError('Please fill in From, To, Subject, and Message fields');
      return;
    }
    setSending(true);
    setError('');
    setSuccess('');

    try {
      let finalBody = body;
      let isEncrypted = false;
      if (encryptEnabled && recipientPublicKey) {
        finalBody = await encryptMessage();
        isEncrypted = true;
      }

      const emailData = {
        from: from,
        to: to,
        subject: subject,
        body: finalBody,
        is_html: false,
        is_encrypted: isEncrypted
      };

      await axios.post('/api/emails/send', emailData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Email sent successfully!');
      setTimeout(() => {
        setTo('');
        setCc('');
        setBcc('');
        setSubject('');
        setBody('');
        setAttachments([]);
        setRecipientPublicKey('');
        setEncryptEnabled(false);
        setSignEnabled(false);
        localStorage.removeItem('emailDraft');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fade-in" style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700' }}>✉️ New Message</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Compose with end-to-end encryption</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {savedDraft && <span style={{ color: '#10B981', fontSize: '14px' }}>✓ Draft saved</span>}
          <button className="btn btn-secondary" onClick={saveDraft} disabled={sending}>💾 Save Draft</button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #EF4444', color: '#EF4444', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10B981', color: '#10B981', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
          ✅ {success}
        </div>
      )}

      <div className="card" style={{ padding: '24px' }}>
        {/* From Field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>From</label>
          {emailAccounts.length > 0 ? (
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px' }}
            >
              {emailAccounts.map(acc => (
                <option key={acc.id} value={acc.email_address}>{acc.email_address}</option>
              ))}
            </select>
          ) : (
            <input type="text" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="your@email.com" style={{ width: '100%', padding: '12px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px' }} />
          )}
        </div>

        {/* To Field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>To</label>
          <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" style={{ width: '100%', padding: '12px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px' }} />
        </div>

        {/* CC/BCC Toggle */}
        <div style={{ marginBottom: '16px' }}>
          <button className="btn btn-ghost" style={{ fontSize: '13px', padding: '4px 8px' }} onClick={() => setShowCcBcc(!showCcBcc)}>
            {showCcBcc ? '▼' : '▶'} CC / BCC
          </button>
        </div>

        {showCcBcc && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>CC</label>
              <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@example.com" style={{ width: '100%', padding: '12px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>BCC</label>
              <input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="bcc@example.com" style={{ width: '100%', padding: '12px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px' }} />
            </div>
          </>
        )}

        {/* Subject Field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Subject</label>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Enter subject..." style={{ width: '100%', padding: '12px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px', fontWeight: '500' }} />
        </div>

        {/* Message Body */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Message</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message here..." rows={10} style={{ width: '100%', padding: '16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '15px', lineHeight: '1.6', resize: 'vertical' }} />
          <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>{body.length} characters</div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)' }}>📎 Attachments ({attachments.length})</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {attachments.map((file, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>📄</span>
                    <span style={{ fontSize: '14px' }}>{file.name}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>({formatFileSize(file.size)})</span>
                  </div>
                  <button className="btn btn-ghost" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={() => removeAttachment(index)}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PGP Encryption Section */}
        <div style={{ background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>🔐</span>
              <span style={{ fontWeight: '600' }}>PGP Encryption</span>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', opacity: selectedKey?.private_key ? 1 : 0.5 }}>
                <input type="checkbox" checked={signEnabled} onChange={(e) => setSignEnabled(e.target.checked)} disabled={!selectedKey?.private_key} />
                <span style={{ fontSize: '14px' }}>Sign</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={encryptEnabled} onChange={(e) => setEncryptEnabled(e.target.checked)} />
                <span style={{ fontSize: '14px' }}>Encrypt</span>
              </label>
            </div>
          </div>

          {signEnabled && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>Signing Key</label>
              <select value={selectedKey?.id || ''} onChange={(e) => { const key = pgpKeys.find(k => k.id === e.target.value); setSelectedKey(key); }} style={{ width: '100%', padding: '10px 14px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontSize: '14px' }}>
                {pgpKeys.map(key => (
                  <option key={key.id} value={key.id}>{key.key_name} {key.is_default ? '(Default)' : ''} - {key.fingerprint?.slice(-8)}</option>
                ))}
              </select>
              {!selectedKey?.private_key && <p style={{ color: '#F59E0B', fontSize: '12px', marginTop: '6px' }}>⚠️ Selected key has no private key for signing</p>}
            </div>
          )}

          {encryptEnabled && (
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>Recipient's Public Key</label>
              <textarea value={recipientPublicKey} onChange={(e) => setRecipientPublicKey(e.target.value)} placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----..." rows={4} style={{ width: '100%', padding: '12px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace' }} />
              {!recipientPublicKey && encryptEnabled && <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '6px' }}>⚠️ Please enter recipient's public key to encrypt</p>}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="file" ref={fileInputRef} onChange={handleAttachment} multiple style={{ display: 'none' }} />
            <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={sending}>📎 Attach</button>
          </div>

          <button className="btn btn-primary" style={{ padding: '14px 32px', fontSize: '16px', fontWeight: '600' }} onClick={handleSend} disabled={sending || (encryptEnabled && !recipientPublicKey)}>
            {sending ? <><span className="loading" style={{ marginRight: '8px' }}></span>Sending...</> : encryptEnabled ? '🔐 Send Encrypted' : signEnabled ? '✍️ Send Signed' : '✉️ Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Compose;

